/**
 * TempoLogo 组件
 * 
 * 统一的 Tempo 品牌标识组件，支持 loading 和 logo 两种模式
 * 
 * 功能特性：
 * - 双模式：旋转模式（loading）+ 静态模式（logo）
 * - 可配置：支持自定义尺寸、颜色、旋转状态
 * - 可替换：未来可轻松替换为真正的品牌logo
 */
import React from 'react';

interface TempoLogoProps {
  /** 是否显示为loading状态（旋转） */
  isLoading?: boolean;
  /** 是否显示为错误状态 */
  isError?: boolean;
  /** 尺寸大小 */
  size?: 'sm' | 'md' | 'lg';
  /** 自定义CSS类名 */
  className?: string;
  /** 自定义颜色 */
  color?: string;
}

export const TempoLogo: React.FC<TempoLogoProps> = ({
  isLoading = false,
  isError = false,
  size = 'md',
  className = '',
  color = 'text-orange-500'
}) => {
  // 根据尺寸获取对应的CSS类
  const getSizeClass = () => {
    // 错误状态下使用更大的尺寸
    if (isError) {
      switch (size) {
        case 'sm':
          return 'h-5 w-5';
        case 'md':
          return 'h-6 w-6';
        case 'lg':
          return 'h-8 w-8';
        default:
          return 'h-6 w-6';
      }
    }

    switch (size) {
      case 'sm':
        return 'h-3 w-3';
      case 'md':
        return 'h-4 w-4';
      case 'lg':
        return 'h-6 w-6';
      default:
        return 'h-4 w-4';
    }
  };

  // 根据状态确定颜色
  const getColor = () => {
    if (isError) {
      return 'text-red-500 dark:text-red-400';
    }
    return color;
  };

  // 组合CSS类名
  const combinedClassName = [
    getSizeClass(),
    getColor(),
    isLoading ? 'animate-spin' : '',
    'flex-shrink-0',
    className
  ].filter(Boolean).join(' ');

  if (isError) {
    // 错误状态显示禁止符号
    return (
      <svg
        className={combinedClassName}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-label="Error"
      >
        {/* 圆形背景 */}
        <circle
          cx="12"
          cy="12"
          r="10"
          fill="currentColor"
          opacity="0.2"
        />
        {/* 圆形边框 */}
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="2.5"
        />
        {/* 禁止斜线 - 更粗更明显 */}
        <path
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          d="m8 8 8 8"
        />
      </svg>
    );
  }

  return (
    <svg
      className={combinedClassName}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-label={isLoading ? 'Loading' : 'Tempo Logo'}
    >
      <path
        fill="currentColor"
        d="M12 2L13.09 8.26L19 7L14.74 11.26L21 12L14.74 12.74L19 17L13.09 15.74L12 22L10.91 15.74L5 17L9.26 12.74L3 12L9.26 11.26L5 7L10.91 8.26L12 2Z"
      />
    </svg>
  );
};

export default TempoLogo;
